import { supabase } from '../supabase';
import { v4 as uuidv4 } from 'uuid';

export const getOrCreateTaskReview = async (taskId: number) => {
  try {
    // 1. Try to get an existing review
    const { data: existingReview, error: getError } = await supabase
      .from('tasks_reviews')
      .select('*')
      .eq('task_id', taskId)
      .single();

    // 2. Error handling
    if (getError && getError.code !== 'PGRST116') {
      // PGRST116 means no rows returned, which is fine in this case
      throw getError;
    }

    // 3. Return existing review if it exists
    if (existingReview) {
      return existingReview;
    }

    // 4. Create a new review if it doesn't exist
    const token = uuidv4();
    const { data: newReview, error: insertError } = await supabase
      .from('tasks_reviews')
      .insert({ task_id: taskId, token })
      .select()
      .single();

    if (insertError) throw insertError;
    return newReview;
  } catch (error) {
    console.error('Error in getOrCreateTaskReview:', error);
    return null;
  }
};

export const updateTaskReview = async (
  taskId: number,
  score: number,
  comment: string
) => {
  try {
    const { data, error } = await supabase
      .from('tasks_reviews')
      .update({ score, comment })
      .eq('task_id', taskId)
      .select()
      .single();

    if (error) {
      console.error('Error updating task review:', error);
      return null;
    }

    console.log('Task review updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Unexpected error in updateTaskReview:', error);
    return null;
  }
};

export const fetchTaskRating = async (taskId: number) => {
  const { data, error } = await supabase
    .from('tasks_reviews')
    .select('score, comment')
    .eq('task_id', taskId)
    .single();

  if (error) {
    console.error('Error fetching task rating:', error);
    return null;
  }
  console.log('Fetched task rating:', data);
  return data;
};

export const updateTaskReviewByToken = async (
  token: string,
  score: number,
  comment: string
) => {
  try {
    const { data, error } = await supabase
      .from('tasks_reviews')
      .update({ score, comment })
      .eq('token', token)
      .select()
      .single();

    if (error) {
      console.error('Error updating task review:', error);
      return null;
    }

    console.log('Task review updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Unexpected error in updateTaskReviewByToken:', error);
    return null;
  }
};

export const fetchLatestCompletedTask = async (askerId: number) => {
  const { data, error } = await supabase
    .from('tasks')
    .select('*')
    .eq('status', 'complete')
    .eq('asker_id', askerId)
    .order('created_at', { ascending: false })
    .limit(1)
    .single();

  if (error) {
    console.error('Error fetching latest completed task:', error);
    return null;
  }
  console.log('Fetched latest completed task:', data);
  return data;
};

export const createOrUpdateReview = async (
  taskId: number,
  score: number,
  comment: string
) => {
  try {
    // First, check if a review already exists for this task
    const { data: existingReview, error: checkError } = await supabase
      .from('tasks_reviews')
      .select('id')
      .eq('task_id', taskId)
      .single();

    if (checkError && checkError.code !== 'PGRST116') {
      console.error('Error checking for existing review:', checkError);
      return null;
    }

    let data;
    let error;

    if (existingReview) {
      // Update existing review
      ({ data, error } = await supabase
        .from('tasks_reviews')
        .update({ score, comment: comment.trim() || null })
        .eq('task_id', taskId)
        .select()
        .single());
    } else {
      // Insert new review
      ({ data, error } = await supabase
        .from('tasks_reviews')
        .insert({
          task_id: taskId,
          score,
          comment: comment.trim() || null,
        })
        .select()
        .single());
    }

    if (error) {
      console.error('Error creating or updating review:', error);
      return null;
    }

    console.log('Review created or updated successfully:', data);
    return data;
  } catch (error) {
    console.error('Unexpected error in createOrUpdateReview:', error);
    return null;
  }
};

export const fetchReviewByToken = async (token: string) => {
  try {
    const { data: reviewData, error: reviewError } = await supabase
      .from('tasks_reviews')
      .select('task_id, score, comment')
      .eq('token', token)
      .single();

    if (reviewError) {
      console.error('Error fetching review:', reviewError);
      throw new Error('Failed to fetch review details');
    }

    return reviewData;
  } catch (err) {
    console.error('Error in fetchReviewByToken:', err);
    throw err;
  }
};

export const getTaskerAverageRating = async (
  taskerId: number
): Promise<number | null> => {
  try {
    const { data, error } = await supabase
      .from('tasks_reviews')
      .select('score, tasks!inner(tasker_id)')
      .eq('tasks.tasker_id', taskerId)
      .not('score', 'is', null)
      .gte('score', 1)
      .lte('score', 5);

    if (error || !data || data.length === 0) {
      return null;
    }

    const totalScore = data.reduce((sum, review) => sum + review.score, 0);
    return Number((totalScore / data.length).toFixed(2));
  } catch (error) {
    console.error('Error in getTaskerAverageRating:', error);
    return null;
  }
};
